'use client';

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import { useTranslation } from 'react-i18next';

export default function ProblemStory() {
  const { t } = useTranslation('home');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const threats = [
    {
      icon: '☠️',
      label: t('problem.threats.pesticides.label'),
      description: t('problem.threats.pesticides.description'),
      color: 'from-red-50 to-orange-50',
      border: 'border-red-100',
      textColor: 'text-red-700'
    },
    {
      icon: '🦠',
      label: t('problem.threats.bacteria.label'),
      description: t('problem.threats.bacteria.description'),
      color: 'from-amber-50 to-yellow-50',
      border: 'border-amber-100',
      textColor: 'text-amber-700'
    },
    {
      icon: '🏭',
      label: t('problem.threats.chemicals.label'),
      description: t('problem.threats.chemicals.description'),
      color: 'from-blue-50 to-slate-50',
      border: 'border-blue-100',
      textColor: 'text-blue-700'
    }
  ];

  return (
    <section
      id="problem"
      ref={ref}
      // FIX 1: Reduced mobile padding and added overflow-x-clip to prevent horizontal wobble
      className="py-16 lg:py-16 relative overflow-x-clip"
      aria-labelledby="problem-heading">

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-white pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-10 lg:mb-16">

          <span className="text-xs font-semibold tracking-widest uppercase text-muted border-b border-border pb-1">
            {t('problem.label')}
          </span>
        </motion.div>

        {/* Split layout */}
        {/* FIX 2: Reduced bottom margin on mobile (mb-16 instead of mb-24) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center mb-20 lg:mb-24">
          <motion.div
            className="col-span-1 lg:col-span-5 flex flex-col lg:flex-col gap-6 lg:gap-10"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* LEFT: Heading */}
            <div className="w-full ">
              <h2
                id="problem-heading"
                className="text-3xl sm:text-5xl lg:text-6xl font-display font-light text-foreground leading-tight mb-4 lg:mb-6"
              >
                {t('problem.title.yourFruitsLook')}{' '}
                <span className="italic text-gradient-green">
                  {t('problem.title.clean')}
                </span>
                <br />
                {t('problem.title.theyAre')}{' '}
                <span className="italic text-red-600">
                  {t('problem.title.not')}
                </span>
              </h2>
            </div>

            {/* RIGHT: Description */}
            <div className="w-full ">
              <p className="text-muted text-base lg:text-lg leading-relaxed font-light">
                {t('problem.description')}
              </p>
            </div>
          </motion.div>

          {/* Divider + Warning */}
          {/* <div className="mt-6 lg:mt-8">
            <div className="divider-organic mb-4 lg:mb-6" />
            <p className="text-foreground font-semibold text-sm lg:text-base">
              {t('problem.warning')}
            </p>
          </div> */}

          {/* Right: Image with overlay */}
          <motion.div
            className="col-span-1 lg:col-span-7 relative w-full items-center justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}>

            {/* <ImgComparisonSlider className='mx-auto outline-none'>
              <img
                slot="first"
                src="/assets/images/dirt_img.png"
                alt="Fresh vegetables and fruits - uncleaned"
                className="w-full outline-none h-auto aspect-[4/3] sm:aspect-[16/10] lg:aspect-video rounded-6xl object-cover"
              />
              <img
                slot="second"
                src="/assets/images/clean_img.png"
                alt="Fresh vegetables and fruits - cleaned"
                className="w-full outline-none h-auto aspect-[4/3] sm:aspect-[16/10] lg:aspect-video rounded-6xl object-cover"
              />
            </ImgComparisonSlider> */}
            <AppImage
              src="/assets/images/before_after.jpeg"
              alt="Vegetables before and after washing with Dr. Detoxer — pesticide and dirt removal comparison"
              width={1200}
              height={675}
              sizes="(max-width: 1024px) 100vw, 700px"
              className="w-full outline-none h-full sm:h-[450px] aspect-[4/3] sm:aspect-[16/10] lg:aspect-video rounded-3xl sm:rounded-6xl object-cover"
            />

            {/* Floating stat */}
            {/* <motion.div
              // FIX 4: Pulled the floating stat inward on mobile (`right-4`) to prevent horizontal overflow clipping
              className="absolute -top-12 -right-4 sm:-top-6 sm:-right-4 lg:-top-12 lg:-right-12 glass px-4 py-3 lg:px-6 lg:py-4 rounded-2xl lg:rounded-3xl shadow-medium z-10"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}>

              <p className="font-display text-2xl lg:text-3xl font-semibold text-red-600">67%</p>
              <p className="text-muted text-[10px] lg:text-xs mt-0.5">{t('problem.stat')}</p>
            </motion.div> */}
          </motion.div>
        </div>

        {/* Threat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
          {threats?.map((threat, i) =>
            <motion.div
              key={threat?.label}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.3 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              // FIX 5: Adjusted padding for mobile screens (p-6 instead of p-8)
              className={`bg-gradient-to-br ${threat?.color} border ${threat?.border} rounded-3xl lg:rounded-4xl p-6 lg:p-8 trust-badge`}>

              <span className="text-3xl lg:text-4xl mb-3 lg:mb-4 block">{threat?.icon}</span>
              <h3 className={`font-display text-xl lg:text-2xl font-semibold mb-2 lg:mb-3 ${threat?.textColor}`}>
                {threat?.label}
              </h3>
              <p className="text-muted text-xs lg:text-sm leading-relaxed">{threat?.description}</p>
            </motion.div>
          )}
        </div>

        {/* Solution bridge */}
        {/* <motion.div
          className="mt-16 lg:mt-24 text-center px-4"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.7 }}>

          <p className="text-muted text-xs lg:text-sm tracking-widest uppercase mb-3 lg:mb-4">{t('problem.solutionLabel')}</p>
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light italic text-foreground leading-tight">
            {t('problem.solutionTitle')}{' '}
            <br className="block sm:hidden" />
            <span className="text-gradient-green not-italic font-semibold">{t('problem.solutionHighlight')}</span>
          </h3>
        </motion.div> */}
      </div>
    </section>
  );
}